import { useState } from "react";
import type { TriviaQuestion } from "../../types";

interface TriviaEditorProps {
  questions: TriviaQuestion[];
  score: string;
  winningScore: string;
  onChange: (data: { questions: TriviaQuestion[]; score: string; winningScore: string }) => void;
}

export function TriviaEditor({ questions, score, winningScore, onChange }: TriviaEditorProps) {
  // Number fields are held as raw text so they can be emptied while typing —
  // coercing on every keystroke is what makes a backspaced field snap back to 1
  // and refuse to clear. The text is only turned into a number when it's used.
  const [addCount, setAddCount] = useState("5");
  // Per-question points text, keyed by index; absent means "show the stored
  // value". Cleared on blur so the field settles back on what was saved.
  const [pointsDraft, setPointsDraft] = useState<Record<number, string>>({});

  const handleAddQuestions = () => {
    const count = Math.min(50, Math.max(1, parseInt(addCount, 10) || 1));
    const newQuestions: TriviaQuestion[] = Array.from({ length: count }, () => ({
      question: "",
      answer: "",
      points: 1,
      correctAnswer: "",
    }));
    onChange({ questions: [...questions, ...newQuestions], score, winningScore });
  };

  const handleRemoveQuestion = (index: number) => {
    // Drafts are keyed by position, so drop them all rather than leave the rows
    // after `index` showing the previous row's text.
    setPointsDraft({});
    onChange({
      questions: questions.filter((_, i) => i !== index),
      score,
      winningScore,
    });
  };

  const handleQuestionChange = (index: number, field: keyof TriviaQuestion, value: string | number) => {
    onChange({
      questions: questions.map((q, i) => (i === index ? { ...q, [field]: value } : q)),
      score,
      winningScore,
    });
  };

  const totalPoints = questions.reduce((sum, q) => sum + (q.points || 0), 0);

  return (
    <div className="custom-response-section">
      <div className="trivia-scores">
        <label className="form-field">
          <span className="form-label">Your Score</span>
          <input
            type="text"
            className="form-input"
            value={score}
            onChange={(e) => onChange({ questions, score: e.target.value, winningScore })}
            placeholder="e.g. 8/10"
          />
        </label>
        <label className="form-field">
          <span className="form-label">Winning Score</span>
          <input
            type="text"
            className="form-input"
            value={winningScore}
            onChange={(e) => onChange({ questions, score, winningScore: e.target.value })}
            placeholder="e.g. 9/10"
          />
        </label>
        <span className="trivia-total-points">Total: {totalPoints} pts</span>
      </div>

      <div className="section-header">
        <span className="form-label">Questions ({questions.length})</span>
        <div className="trivia-add-controls">
          <input
            type="number"
            inputMode="numeric"
            className="form-input trivia-count-input"
            value={addCount}
            onChange={(e) => setAddCount(e.target.value)}
            onBlur={() => setAddCount(String(Math.min(50, Math.max(1, parseInt(addCount, 10) || 1))))}
            min={1}
            max={50}
            aria-label="How many questions to add"
          />
          <button type="button" className="btn btn-small btn-secondary" onClick={handleAddQuestions}>
            + Add
          </button>
        </div>
      </div>

      {questions.map((q, i) => (
        <div key={i} className="trivia-question-row">
          <div className="trivia-question-header">
            <span className="trivia-question-num">Q{i + 1}</span>
            <button type="button" className="btn-icon" onClick={() => handleRemoveQuestion(i)} title="Remove">
              &times;
            </button>
          </div>
          <input
            type="text"
            className="form-input"
            value={q.question}
            onChange={(e) => handleQuestionChange(i, "question", e.target.value)}
            placeholder="Question"
          />
          <div className="trivia-answer-row">
            <input
              type="text"
              className="form-input"
              value={q.answer}
              onChange={(e) => handleQuestionChange(i, "answer", e.target.value)}
              placeholder="Your answer"
            />
            <input
              type="number"
              inputMode="numeric"
              className="form-input trivia-points-input"
              value={pointsDraft[i] ?? String(q.points ?? 0)}
              onChange={(e) => {
                setPointsDraft((prev) => ({ ...prev, [i]: e.target.value }));
                handleQuestionChange(i, "points", parseInt(e.target.value, 10) || 0);
              }}
              onBlur={() =>
                setPointsDraft((prev) => {
                  const next = { ...prev };
                  delete next[i];
                  return next;
                })
              }
              min={0}
              title="Points"
            />
          </div>
          <input
            type="text"
            className="form-input"
            value={q.correctAnswer}
            onChange={(e) => handleQuestionChange(i, "correctAnswer", e.target.value)}
            placeholder="Correct answer (optional)"
          />
        </div>
      ))}
    </div>
  );
}

/** Read-only display of trivia results */
export function TriviaDisplay({
  questions,
  score,
  winningScore,
}: {
  questions: TriviaQuestion[];
  score: string;
  winningScore: string;
}) {
  const [showQuestions, setShowQuestions] = useState(false);
  const totalPoints = questions.reduce((sum, q) => sum + (q.points || 0), 0);

  return (
    <div className="custom-response-display">
      <h4>Trivia</h4>
      <div className="trivia-score-display">
        {score && <span className="trivia-score">Score: <strong>{score}</strong></span>}
        {winningScore && <span className="trivia-score">Winning: <strong>{winningScore}</strong></span>}
        <span className="trivia-score">Total points: {totalPoints}</span>
      </div>

      {questions.length > 0 && (
        <>
          <button
            type="button"
            className="btn btn-small btn-secondary"
            onClick={() => setShowQuestions(!showQuestions)}
          >
            {showQuestions ? "Hide" : "Show"} {questions.length} question{questions.length !== 1 ? "s" : ""}
          </button>

          {showQuestions && (
            <ul className="trivia-questions-list">
              {questions.map((q, i) => (
                <li key={i} className="trivia-question-display">
                  <span className="trivia-question-num">Q{i + 1}</span>
                  <div className="trivia-question-content">
                    {q.question && <span className="trivia-q-text">{q.question}</span>}
                    <span className="trivia-answer">
                      Your answer: {q.answer || "—"}
                      {q.correctAnswer && (
                        <span className={q.answer === q.correctAnswer ? "trivia-correct" : "trivia-incorrect"}>
                          {" "}(Correct: {q.correctAnswer})
                        </span>
                      )}
                    </span>
                    <span className="text-muted">{q.points} pt{q.points !== 1 ? "s" : ""}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
